import { ImageEditorTool } from './ImageEditorTool';

/**
 * ImageCropTool - Crop images to specific areas
 * Extends ImageEditorTool with crop-specific functionality
 */
export class ImageCropTool extends ImageEditorTool {
  private _currentAspectRatioPreset: string = 'free';

  constructor() {
    super('crop');
  }

  async init(): Promise<void> {
    console.log('[ImageCropTool] Starting initialization...');
    await super.init();
    console.log('[ImageCropTool] Image Crop Tool initialized successfully');
  }

  get currentAspectRatioPreset(): string {
    return this._currentAspectRatioPreset;
  }

  protected setAspectRatioPreset(ratio: string): void {
    this._currentAspectRatioPreset = ratio;
    
    if (!this.currentImage || ratio === 'free') {
      return;
    }

    // Parse the ratio string (e.g., "16:9" -> 16/9)
    const parts = ratio.split(':');
    if (parts.length !== 2) return;
    
    const aspectWidth = parseInt(parts[0]);
    const aspectHeight = parseInt(parts[1]);
    const targetRatio = aspectWidth / aspectHeight;

    // Calculate the largest crop area that fits the aspect ratio
    const imageWidth = this.originalDimensions.width;
    const imageHeight = this.originalDimensions.height;
    const currentRatio = imageWidth / imageHeight;

    let newWidth: number;
    let newHeight: number;

    if (currentRatio > targetRatio) {
      // Image is wider than target ratio, constrain by height
      newHeight = imageHeight;
      newWidth = newHeight * targetRatio;
    } else {
      // Image is taller than target ratio, constrain by width
      newWidth = imageWidth;
      newHeight = newWidth / targetRatio;
    }

    // Center the crop area
    const x = (imageWidth - newWidth) / 2;
    const y = (imageHeight - newHeight) / 2;

    this.cropArea = {
      x: x,
      y: y,
      width: newWidth,
      height: newHeight
    };

    this.newDimensions = {
      width: Math.round(newWidth),
      height: Math.round(newHeight)
    };

    this.updateCropOverlay();
    this.updateDimensionDisplay();
  }

  getToolName(): string {
    return 'Image Crop';
  }
}

// Module loaded
console.log('[ImageCropTool] Module loaded, document.readyState:', document.readyState);

// Initialize the tool when DOM is ready
if (document.readyState === 'loading') {
  console.log('[ImageCropTool] DOM still loading, waiting for DOMContentLoaded');
  document.addEventListener('DOMContentLoaded', () => {
    console.log('[ImageCropTool] DOMContentLoaded fired, creating tool instance');
    const tool = new ImageCropTool();
    tool.init();
  });
} else {
  console.log('[ImageCropTool] DOM already loaded, creating tool instance immediately');
  const tool = new ImageCropTool();
  tool.init();
}
